"use client";

import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Home, ArrowLeft, Search } from 'lucide-react';
import Link from 'next/link';

export default function NotFound() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/20">
            <div className="container mx-auto px-4">
                <div className="max-w-2xl mx-auto text-center space-y-8">
                    {/* 404 Icon */}
                    <div className="flex justify-center">
                        <div className="relative">
                            <div className="w-32 h-32 bg-primary/10 rounded-full flex items-center justify-center">
                                <AlertTriangle className="w-16 h-16 text-primary" />
                            </div>
                            <div className="absolute -top-2 -right-2 w-8 h-8 bg-destructive rounded-full flex items-center justify-center">
                                <span className="text-destructive-foreground text-sm font-bold">404</span>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <Card className="border-0 shadow-lg">
                        <CardHeader className="text-center space-y-4">
                            <CardTitle className="text-4xl font-bold text-foreground">
                                Page Not Found
                            </CardTitle>
                            <p className="text-lg text-muted-foreground max-w-md mx-auto">
                                The page you&apos;re looking for doesn&apos;t exist or has been moved.
                                Let&apos;s get you back on track.
                            </p>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button asChild className="flex-1 sm:flex-none">
                                    <Link href="/">
                                        <Home className="w-4 h-4 mr-2" />
                                        Go Home
                                    </Link>
                                </Button>
                                <Button variant="secondary" asChild className="flex-1 sm:flex-none">
                                    <Link href="/library">
                                        <Search className="w-4 h-4 mr-2" />
                                        Browse Library
                                    </Link>
                                </Button>
                            </div>

                            {/* Back Button */}
                            <div className="pt-4">
                                <Button
                                    variant="link"
                                    onClick={() => window.history.back()}
                                    className="text-muted-foreground hover:text-foreground"
                                >
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    Go Back
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Helpful Links */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                        <Card className="hover:shadow-md transition-shadow cursor-pointer">
                            <CardContent className="p-4 text-center">
                                <h3 className="font-semibold text-sm mb-2">Dashboard</h3>
                                <p className="text-xs text-muted-foreground">
                                    View your analytics and insights
                                </p>
                            </CardContent>
                        </Card>
                        <Card className="hover:shadow-md transition-shadow cursor-pointer">
                            <CardContent className="p-4 text-center">
                                <h3 className="font-semibold text-sm mb-2">Spaces</h3>
                                <p className="text-xs text-muted-foreground">
                                    Manage your workspaces
                                </p>
                            </CardContent>
                        </Card>
                        <Card className="hover:shadow-md transition-shadow cursor-pointer">
                            <CardContent className="p-4 text-center">
                                <h3 className="font-semibold text-sm mb-2">Settings</h3>
                                <p className="text-xs text-muted-foreground">
                                    Configure your preferences
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}
